import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { siteImagery } from '../config/siteImagery';
import { Container } from '../components/layout/Primitives';
import { USE_API } from '../config/env';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchCollections } from '../store/actions/collectionActions';
import { Button } from '../components/ui/Button';

function collectionCardImage(collection) {
  const u =
    typeof collection?.image === 'string' ? collection.image.trim() : '';
  return u.startsWith('http') || u.startsWith('data:') ? u : siteImagery.heroMain;
}

export default function Collections() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const apiCollections = useAppSelector((s) => s.collection.list);
  const apiLoading = useAppSelector((s) => s.collection.listLoading);
  const listError = useAppSelector((s) => s.collection.listError);

  useEffect(() => {
    if (!USE_API) return undefined;
    dispatch(fetchCollections());
    return undefined;
  }, [dispatch]);

  const slugged = apiCollections.filter((c) => c && c.slug);
  const showEmpty =
    USE_API && !apiLoading && !listError && slugged.length === 0;

  return (
    <div className="animate-fade-in">
      <section style={{ background: 'hsla(38,25%,90%,0.4)', borderBottom: '1px solid hsl(35,18%,84%)' }}>
        <Container style={{ padding: '3.5rem 1.5rem', textAlign: 'center' }}>
          <p className="font-mono-luxe" style={{ margin: '0 0 1rem', fontSize: '11px', letterSpacing: '0.3em', color: '#524D46' }}>
            {t('modamai.collections.kicker')}
          </p>
          <h1 className="font-display" style={{ margin: 0, fontSize: 'clamp(2.5rem,6vw,4.5rem)', lineHeight: 0.95 }}>
            {t('modamai.collections.title')}
          </h1>
          <p style={{ margin: '1.25rem auto 0', maxWidth: '36rem', opacity: 0.65 }}>
            {t('modamai.collections.lead')}
          </p>
        </Container>
      </section>

      <Container style={{ padding: '3rem 1.5rem 4rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        {!USE_API ? (
          <p style={{ textAlign: 'center', margin: 0, opacity: 0.72, fontSize: '0.9rem' }}>
            {t('modamai.collections.apiOff')}
          </p>
        ) : null}

        {USE_API && listError ? (
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <p style={{ margin: '0 0 1rem', color: 'hsl(0,45%,38%)', fontSize: '0.9rem' }}>{listError}</p>
            <Button type="button" $variant="outlineLuxe" onClick={() => dispatch(fetchCollections())}>
              {t('modamai.collections.retry')}
            </Button>
          </div>
        ) : null}

        {USE_API && apiLoading && slugged.length === 0 && !listError ? (
          <p
            style={{ textAlign: 'center', margin: '0', fontSize: 13, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#524D46' }}
            aria-busy="true"
          >
            {t('modamai.collections.loading')}
          </p>
        ) : null}

        {showEmpty ? (
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <p style={{ margin: '0 0 1rem', opacity: 0.72, fontSize: '0.9rem' }}>
              {t('modamai.collections.empty')}
            </p>
            <Button as={Link} to="/shop" $variant="luxe">
              {t('modamai.collections.browseShop')}
            </Button>
          </div>
        ) : null}

        {USE_API && slugged.length > 0 ? (
          <div style={{ display: 'grid', gap: '0.85rem' }}>
            <p
              className="font-mono-luxe"
              style={{ margin: 0, fontSize: '11px', letterSpacing: '0.26em', textTransform: 'uppercase', color: '#524D46' }}
            >
              {t('modamai.collections.shopCurations')}
            </p>
            <div
              style={{
                display: 'grid',
                gap: '1rem',
              }}
              className="api-col-grid"
            >
              <style>{`@media(min-width:640px){.api-col-grid{grid-template-columns:repeat(2,1fr)!important}}@media(min-width:1024px){.api-col-grid{grid-template-columns:repeat(3,1fr)!important}}`}</style>
              {slugged.map((c) => (
                <Link
                  key={String(c.slug || c._id)}
                  to={`/collection/${encodeURIComponent(String(c.slug))}`}
                  style={{
                    position: 'relative',
                    display: 'block',
                    aspectRatio: '3/4',
                    overflow: 'hidden',
                    background: 'hsl(38,25%,90%)',
                  }}
                >
                  <img
                    src={collectionCardImage(c)}
                    alt=""
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background:
                        'linear-gradient(to top, hsla(0,0%,0%,0.8), hsla(0,0%,0%,0.1), transparent)',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      padding: '1.35rem',
                      color: 'hsl(40,30%,96%)',
                    }}
                  >
                    <p
                      style={{
                        margin: '0 0 0.25rem',
                        fontSize: 10,
                        letterSpacing: '0.28em',
                        textTransform: 'uppercase',
                        color: '#F5F2EB',
                      }}
                    >
                      {t('modamai.collections.pieceCount', {
                        count: Number(c.productCount != null ? c.productCount : 0),
                      })}
                    </p>
                    <h3 className="font-display" style={{ margin: 0, fontSize: 'clamp(1.45rem,3.5vw,2rem)', lineHeight: 1.05 }}>
                      {c.name}
                    </h3>
                    {c.subtitle ? (
                      <p style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', opacity: 0.78 }}>{c.subtitle}</p>
                    ) : null}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ) : null}
      </Container>
    </div>
  );
}

